import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import type { RootState } from "../../state/store";
import { useSignalRContext } from "../Contexts/SignalRContext";
import { useOnlineStatus } from "../Hooks/useOnlineStatus";
import { resolveConnectionIssueKind, hasRecovered, ConnectionIssueKind } from "../../utilities/helpers/connectionStatus";

// Note: a single, non-blocking banner for any live-connection problem. No
// internet OR a failed/reconnecting SignalR connection shows one warning
// snackbar (offline wins, so only one ever shows); when everything clears it
// shows a brief "Connection restored". Renders nothing itself.
export function ConnectionBanner() {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { connection, connectionError, isReconnecting } = useSignalRContext();
  const isAuthenticated = useSelector((state: RootState) => state.authReducer.isAuthenticated);
  const isOnline = useOnlineStatus();

  const snackbarId = useRef<string | number | null>(null);
  const issueKindRef = useRef<ConnectionIssueKind>(null);
  // Note: a connection lost without an error is only recognisable as a loss if we know there was one.
  const hasEverConnected = useRef(false);
  if (connection) hasEverConnected.current = true;

  const recovered = hasRecovered({
    isOnline,
    isAuthenticated,
    hasLiveConnection: !!connection,
  });

  const connectionIssueKind = resolveConnectionIssueKind({
    isOnline,
    isAuthenticated,
    isReconnecting,
    hasConnectionError: !!connectionError,
    hasLiveConnection: !!connection,
    hasEverConnected: hasEverConnected.current,
  });

  useEffect(() => {
    if (connectionIssueKind) {
      if (snackbarId.current === null || issueKindRef.current !== connectionIssueKind) {
        if (snackbarId.current !== null) closeSnackbar(snackbarId.current);
        snackbarId.current = enqueueSnackbar(
          <div>
            {connectionIssueKind === "offline" ? "No internet connection" : "Connection lost. Reconnecting"}
            <FontAwesomeIcon className="ml-2" icon={faSpinner} spin />
          </div>,
          {
            variant: "warning",
            anchorOrigin: { vertical: "top", horizontal: "center" },
            persist: true,
            preventDuplicate: true,
          }
        );
        issueKindRef.current = connectionIssueKind;
      }
    } else if (snackbarId.current !== null && recovered) {
      closeSnackbar(snackbarId.current);
      snackbarId.current = null;
      issueKindRef.current = null;
      enqueueSnackbar("Connection restored", {
        variant: "success",
        anchorOrigin: { vertical: "top", horizontal: "center" },
        autoHideDuration: 3000,
      });
    }
  }, [connectionIssueKind, recovered, enqueueSnackbar, closeSnackbar]);

  return null;
}

export default ConnectionBanner;
